/**
 * Thin fetch wrapper for /api/admin/* endpoints. All calls share cookies
 * (BA session) via `credentials: 'include'` and surface non-2xx as a
 * typed `ApiError` carrying the parsed JSON body.
 */

const API_PATH = import.meta.env.VITE_API_PATH ?? '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseResponse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  query?: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const qs = query
    ? '?' +
      Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  const res = await fetch(`${API_PATH}${path}${qs}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'content-type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const parsed = await parseResponse(res);
  if (!res.ok) {
    const errMsg =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, parsed, errMsg);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | number | boolean | undefined>) =>
    request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
};

// ─── Admin endpoint shapes ───────────────────────────────────────────

export interface AdminOrgRow {
  id: string;
  name: string;
  slug: string;
  createdAt: string | number;
  memberCount: number;
  plan: string | null;
  status: 'active' | 'past_due' | 'suspended' | null;
  suspendedAt: number | null;
  suspendedReason: string | null;
  lastRunAt: number | null;
}

export interface AdminOrgDetail {
  org: {
    id: string;
    name: string;
    slug: string;
    createdAt: string | number;
    metadata: string | null;
    plan: string | null;
    status: 'active' | 'past_due' | 'suspended' | null;
    suspendedAt: number | null;
    suspendedReason: string | null;
    suspendedBy: string | null;
    deletedAt: number | null;
    notes: string | null;
    stripeCustomerId: string | null;
    currentPeriodEnd: number | null;
  };
  members: Array<{
    id: string;
    userId: string;
    role: string;
    createdAt: string | number;
    email: string;
    name: string | null;
  }>;
  recentRuns: Array<{
    id: string;
    flowId: string;
    status: string;
    startedAt: string | null;
    completedAt: string | null;
  }>;
  recentAudit: Array<{
    id: string;
    userId: string | null;
    adminUserId: string | null;
    eventType: string;
    resourceType: string | null;
    resourceId: string | null;
    actorType: string | null;
    createdAt: number;
    metadata: string | null;
  }>;
}

export interface AdminAuditRow {
  id: string;
  organizationId: string | null;
  userId: string | null;
  adminUserId: string | null;
  eventType: string;
  resourceType: string | null;
  resourceId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: unknown;
  createdAt: number;
  actorType: string | null;
  idempotencyKey: string | null;
}

export interface AdminDlqRow {
  id: string;
  organizationId: string | null;
  jobType: string;
  jobId: string | null;
  attempts: number;
  firstSeen: number;
  lastSeen: number;
  requeuedAt: number | null;
  requeuedBy: string | null;
  discardedAt: number | null;
  discardedBy: string | null;
  discardReason: string | null;
  errorMessage: string | null;
  original_job: unknown;
  error_chain: unknown;
}

export interface AdminBillingOverviewSummary {
  totalOrgs: number;
  paidOrgs: number;
  pastDueOrgs: number;
  suspendedOrgs: number;
  anomalyCount: number;
  mrrByCurrency: Array<{
    currency: string;
    amount: number;
  }>;
}

export interface AdminBillingOverviewRow {
  organizationId: string;
  name: string;
  slug: string;
  createdAt: string | number;
  localPlan: string;
  accessStatus: 'active' | 'past_due' | 'suspended';
  stripeCustomerId: string | null;
  stripeCustomerEmail: string | null;
  stripeSubscriptionId: string | null;
  stripeSubscriptionStatus: string | null;
  recurringAmount: number | null;
  recurringCurrency: string | null;
  billingCycle: 'monthly' | 'annual' | string | null;
  currentPeriodEnd: number | null;
  latestInvoiceId: string | null;
  latestInvoiceStatus: string | null;
  latestInvoiceAmountDue: number | null;
  latestInvoiceAmountPaid: number | null;
  latestInvoiceCurrency: string | null;
  latestInvoiceCreatedAt: number | null;
  lastWebhookAt: number | null;
  lastSyncedAt: number | null;
  anomalyFlags: string[];
}

export interface AdminBillingOverviewResponse {
  stripeConfigured: boolean;
  stripeDashboardBaseUrl: string | null;
  summary: AdminBillingOverviewSummary;
  total: number;
  limit: number;
  offset: number;
  data: AdminBillingOverviewRow[];
}

export interface AdminStripeInvoiceSummary {
  id: string;
  status: string | null;
  amountDue: number | null;
  amountPaid: number | null;
  amountRemaining: number | null;
  currency: string | null;
  createdAt: number | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface AdminStripeBillingDetail {
  customer: {
    id: string;
    email: string | null;
    name: string | null;
    description: string | null;
    metadataOrganizationId: string | null;
  };
  subscription: {
    id: string;
    status: string | null;
    billingCycle: 'monthly' | 'annual' | string | null;
    recurringAmount: number | null;
    recurringCurrency: string | null;
    priceId: string | null;
    currentPeriodStart: number | null;
    currentPeriodEnd: number | null;
    cancelAt: number | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  defaultPaymentMethod: {
    id: string;
    brand: string | null;
    last4: string | null;
  } | null;
  invoices: AdminStripeInvoiceSummary[];
}

export interface AdminBillingDetailResponse {
  stripeConfigured: boolean;
  stripeDashboardBaseUrl: string | null;
  data: {
    organizationId: string;
    name: string;
    slug: string;
    createdAt: string | number;
    local: {
      plan: string;
      accessStatus: 'active' | 'past_due' | 'suspended';
      rawStatus: string | null;
      stripeCustomerId: string | null;
      stripeSubscriptionId: string | null;
      currentPeriodEnd: number | null;
      cancelAt: number | null;
      cancelAtPeriodEnd: boolean;
      billingCycle: string | null;
    };
    snapshot: {
      stripeCustomerId: string | null;
      stripeCustomerEmail: string | null;
      stripeSubscriptionId: string | null;
      stripeSubscriptionStatus: string | null;
      recurringAmount: number | null;
      recurringCurrency: string | null;
      billingCycle: string | null;
      currentPeriodStart: number | null;
      currentPeriodEnd: number | null;
      cancelAt: number | null;
      cancelAtPeriodEnd: boolean;
      latestInvoiceId: string | null;
      latestInvoiceStatus: string | null;
      latestInvoiceAmountDue: number | null;
      latestInvoiceAmountPaid: number | null;
      latestInvoiceCurrency: string | null;
      latestInvoiceCreatedAt: number | null;
      defaultPaymentMethodBrand: string | null;
      defaultPaymentMethodLast4: string | null;
      lastWebhookAt: number | null;
      lastSyncedAt: number | null;
    };
    recentUsage: Array<{
      id: string;
      day: string;
      metric: string;
      count: number;
      cost: number;
      recordedAt: number;
    }>;
    recentBillingEvents: Array<{
      id: string;
      type: string;
      stripeEventId: string;
      receivedAt: number;
      processedAt: number | null;
    }>;
    live: AdminStripeBillingDetail | null;
    anomalyFlags: string[];
  };
}

// ─── Sandboxes (agent workspaces) ────────────────────────────────────

export interface AdminSandboxRow {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  name: string;
  workspaceProviderId: string;
  visibility: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sessionCount: number;
  lastSessionAt: string | null;
}

export interface AdminSandboxBulkDeleteRequest {
  reason: string;
  scope: 'org' | 'all';
  orgId?: string;
}

export interface AdminSandboxBulkDeleteResponse {
  ok: true;
  scope: 'org' | 'all';
  orgId: string | null;
  workspacesDeleted: number;
}

export const adminApi = {
  listOrgs: (q: { search?: string; limit?: number; offset?: number }) =>
    api.get<{ data: AdminOrgRow[]; limit: number; offset: number }>('/admin/orgs', q),

  getOrg: (id: string) => api.get<{ data: AdminOrgDetail }>(`/admin/orgs/${id}`),

  suspendOrg: (id: string, reason: string) =>
    api.post<{ ok: true; suspendedAt: number }>(`/admin/orgs/${id}/suspend`, { reason }),

  unsuspendOrg: (id: string, reason: string) =>
    api.post<{ ok: true }>(`/admin/orgs/${id}/unsuspend`, { reason }),

  forceCancelRun: (runId: string, reason: string) =>
    api.post<{ ok: true }>(`/admin/flow-runs/${runId}/force-cancel`, { reason }),

  searchAudit: (q: {
    orgId?: string;
    userId?: string;
    eventType?: string;
    before?: number;
    limit?: number;
  }) => api.get<{ data: AdminAuditRow[]; nextBefore: number | null }>('/admin/audit', q),

  listDlq: (q: { orgId?: string; status?: string; limit?: number }) =>
    api.get<{ data: AdminDlqRow[] }>('/admin/dlq', q),

  getBillingOverview: (q: {
    search?: string;
    plan?: string;
    accessStatus?: string;
    subscriptionStatus?: string;
    invoiceStatus?: string;
    anomaliesOnly?: boolean;
    limit?: number;
    offset?: number;
  }) => api.get<AdminBillingOverviewResponse>('/admin/billing/overview', q),

  getBillingOrg: (id: string) => api.get<AdminBillingDetailResponse>(`/admin/billing/orgs/${id}`),

  refreshBillingOrg: (id: string) =>
    api.post<AdminBillingDetailResponse & { ok: true }>(`/admin/billing/orgs/${id}/refresh`),

  requeueDlq: (id: string) => api.post<{ ok: true }>(`/admin/dlq/${id}/requeue`),

  discardDlq: (id: string, reason: string) =>
    api.post<{ ok: true }>(`/admin/dlq/${id}/discard`, { reason }),

  // Sandboxes (agent workspaces) — list + per-row delete + scoped bulk delete.
  // See routes/admin.ts for the audit semantics. Deletion cascades to all
  // per-workspace and per-session rows (messages, attachments, file_edits,
  // audit events, pending human actions, etc.). The Sandbox DO instances
  // themselves are not explicitly destroyed; they go idle and shut down via
  // the workspace provider's `sleepAfter` window.
  listSandboxes: (q: { orgId?: string; limit?: number; offset?: number } = {}) =>
    api.get<{ data: AdminSandboxRow[]; limit: number; offset: number }>('/admin/sandboxes', q),

  deleteSandbox: (id: string, reason: string) =>
    request<{ ok: true; workspaceId: string; changes: number }>(
      'DELETE',
      `/admin/sandboxes/${id}`,
      { reason },
    ),

  bulkDeleteSandboxes: (input: AdminSandboxBulkDeleteRequest) =>
    api.post<AdminSandboxBulkDeleteResponse>('/admin/sandboxes/bulk-delete', input),
};
